/**
 * On-demand uplink measurement card (DeviceDetail, phones).
 *
 * "Run test" enqueues `run_speed_test`; the phone uploads a throwaway
 * incompressible blob through the REAL tus path and reports
 * {mbps, duration_s, bytes, transport} on the command result, which
 * the backend also lands in fleet_speed_tests — this card shows the
 * newest result plus a history sparkline from that table. The phone
 * refuses while recording, so the error path is a normal outcome.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Gauge } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import HelpTip from './HelpTip';
import api from '../services/api';
import { FleetCommand, FleetDevice, FleetEvent } from '../types';

interface SpeedTestRow {
  mbps: number;
  bytes: number;
  duration_s: number;
  transport: string | null;
  created_at: string;
}

/** Inline sparkline over the (chronological) mbps series. */
const Sparkline: React.FC<{ values: number[] }> = ({ values }) => {
  if (values.length < 2) return null;
  const w = 120;
  const h = 28;
  const max = Math.max(...values, 1);
  const pts = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * (w - 2) + 1;
      const y = h - 2 - (v / max) * (h - 6);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      className="shrink-0"
      aria-label="Speed-test history"
    >
      <polyline
        points={pts}
        fill="none"
        stroke="var(--accent)"
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
};

const SpeedTestCard: React.FC<{ device: FleetDevice }> = ({ device }) => {
  const [history, setHistory] = useState<SpeedTestRow[]>([]);
  const [pendingUuid, setPendingUuid] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setHistory(await api.getDeviceSpeedTests(device.device_id, { limit: 20 }));
    } catch {
      // history is best-effort display; the run button still works
    }
  }, [device.device_id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!pendingUuid) return;
    const unsub = api.subscribeFleetEvents((evt: FleetEvent) => {
      if (evt.type !== 'command_update') return;
      const cmd: FleetCommand = evt.command;
      if (cmd.uuid !== pendingUuid) return;
      if (cmd.status === 'done') {
        setPendingUuid(null);
        void refresh();
      } else if (cmd.status === 'failed' || cmd.status === 'timed_out') {
        setPendingUuid(null);
        setError(cmd.error || `command ${cmd.status}`);
      }
    });
    return unsub;
  }, [pendingUuid, refresh]);

  const run = async () => {
    setError(null);
    try {
      const cmd = await api.enqueueFleetCommand(device.device_id, 'run_speed_test', {
        mb: 20,
      });
      setPendingUuid(cmd.uuid);
    } catch (e) {
      setError((e as { message?: string })?.message ?? String(e));
    }
  };

  const last = history[0];
  const series = [...history].reverse().map((r) => r.mbps);

  return (
    <Card className="border-border bg-bg-surface">
      <CardContent className="p-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <h3 className="text-base font-medium text-accent-purple">Speed test</h3>
            <HelpTip>
              Uploads a throwaway 20 MB blob through the exact path real
              segments take and measures the rate — the honest &quot;what
              can this SIM do from here&quot; number, unlike the phone&apos;s
              own network estimate. Only while idle; takes up to a few
              minutes on a weak link.
            </HelpTip>
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={!device.online || pendingUuid != null}
            onClick={() => void run()}
          >
            {pendingUuid != null ? (
              <>
                <Spinner size={14} />
                measuring…
              </>
            ) : (
              <>
                <Gauge className="size-4" />
                Run test
              </>
            )}
          </Button>
        </div>

        {error && <p className="mb-2 text-xs text-status-error">{error}</p>}

        {last ? (
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-mono text-2xl text-fg">
                {last.mbps.toFixed(1)}
                <span className="ml-1 text-xs text-fg-muted">Mb/s</span>
              </div>
              <div className="text-xs text-fg-muted">
                {new Date(last.created_at).toLocaleString()}
                {last.transport ? ` · ${last.transport}` : ''}
                {` · ${(last.bytes / 1e6).toFixed(0)} MB in ${last.duration_s.toFixed(1)} s`}
              </div>
            </div>
            <Sparkline values={series} />
          </div>
        ) : (
          <p className="text-xs text-fg-muted">
            no speed tests yet — run one while the phone is idle
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default SpeedTestCard;
